/**
 * Commission rules.
 *
 * A rule is what turns a listed product into something a creator can earn on.
 * Two things about the UI here are deliberate:
 *
 *  - Rules are shown with their scope, because resolution is by SPECIFICITY:
 *    a PRODUCT rule beats a CATEGORY rule beats a BRAND rule. Seeing them in
 *    one list is how you understand which one will actually apply, so the
 *    scope pill is colour-coded by how specific it is and carries the
 *    explanation on hover.
 *  - Editing a rate is NOT how you change a rate. Commissions already
 *    computed snapshot their rule, so editing does not rewrite history -- but
 *    it silently changes every future one. The form says so.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import SectionCard from '../../_helpers/reusableComponents/design/SectionCard';
import EmptyState from '../../_helpers/reusableComponents/design/EmptyState';
import StatusPill from '../../_helpers/reusableComponents/design/StatusPill';
import PrimaryButton, {
  SecondaryButton,
} from '../../_helpers/reusableComponents/design/PrimaryButton';
import {
  palette,
  radius,
  shadow,
  motion,
  typography,
} from '../../_helpers/reusableComponents/design/designTokens';
import CommerceService, { readApiError } from '../../_services/commerce/CommerceService';

const SCOPE_HELP = {
  BRAND: 'Applies to every product you list, unless a more specific rule wins.',
  CATEGORY: 'Applies to every product in this category, across all brands.',
  PRODUCT: 'Applies to one product only. Beats category and brand rules.',
};

// Specificity read as colour: the broadest scope is the quietest, the one that
// overrides everything else is the loudest. Scanning the column tells you
// which rule wins before reading a word of it.
const SCOPE_STYLE = {
  BRAND: { fg: palette.muted, bg: '#F3F4F6' },
  CATEGORY: { fg: '#1E3A8A', bg: palette.infoSoft },
  PRODUCT: { fg: '#9F1239', bg: palette.brandSoft },
};

const EMPTY_FORM = {
  name: '',
  scope: 'BRAND',
  category: '',
  product: '',
  rate_type: 'PERCENT',
  rate_value: '',
  min_order_value: '',
  max_commission_amount: '',
  attribution_window_days: 7,
  priority: 0,
  is_active: true,
};

const FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: `${radius.sm}px`,
    bgcolor: palette.surface,
    '& fieldset': { borderColor: palette.line },
    '&:hover fieldset': { borderColor: palette.hint },
    '&.Mui-focused fieldset': { borderColor: palette.brand, borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: palette.brand },
};

const HEAD_ROW_SX = {
  '& th': {
    ...typography.eyebrow,
    bgcolor: palette.surfaceAlt,
    borderBottom: `1px solid ${palette.line}`,
    py: 1.25,
    whiteSpace: 'nowrap',
  },
};

const BODY_ROW_SX = {
  transition: `background-color ${motion.fast}`,
  '&:hover': { backgroundColor: palette.brandSofter },
  '&:last-of-type td': { borderBottom: 0 },
};

const ScopePill = ({ scope }) => {
  const style = SCOPE_STYLE[scope] || SCOPE_STYLE.BRAND;
  return (
    <Tooltip arrow title={SCOPE_HELP[scope] || ''}>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          px: 1.125, py: 0.25,
          bgcolor: style.bg,
          color: style.fg,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.04em',
          borderRadius: '999px',
          whiteSpace: 'nowrap',
        }}
      >
        {scope}
      </Box>
    </Tooltip>
  );
};

const CommissionRules = ({ onNotify }) => {
  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const notify = useCallback(
    (message, severity = 'info') => {
      if (onNotify) onNotify(message, severity);
    },
    [onNotify]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ruleRes, catRes, prodRes] = await Promise.all([
        CommerceService.listRules(),
        CommerceService.listCategories(),
        CommerceService.listProducts({ limit: 100 }),
      ]);
      setRules(ruleRes.data.results || ruleRes.data);
      setCategories(catRes.data.results || catRes.data);
      setProducts(prodRes.data.results || prodRes.data);
    } catch (error) {
      notify(readApiError(error, 'Could not load commission rules.'), 'error');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (field) => (event) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        scope: form.scope,
        rate_type: form.rate_type,
        rate_value: String(form.rate_value),
        attribution_window_days: Number(form.attribution_window_days) || 7,
        priority: Number(form.priority) || 0,
        is_active: true,
        // The rule is live from the moment it is created. Backdating would
        // change what a sale placed an hour ago earns, which the ledger has
        // already accounted for.
        effective_from: new Date().toISOString(),
      };
      if (form.scope === 'CATEGORY') payload.category = form.category;
      if (form.scope === 'PRODUCT') payload.product = form.product;
      if (form.min_order_value) payload.min_order_value = String(form.min_order_value);
      if (form.max_commission_amount) {
        payload.max_commission_amount = String(form.max_commission_amount);
      }

      await CommerceService.createRule(payload);
      notify('Commission rule created.', 'success');
      setDialogOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (error) {
      notify(readApiError(error, 'Could not create the rule.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  // Loading, empty and populated are three peer states; chaining them as
  // nested ternaries inside the JSX read as a single condition.
  let ruleList;
  if (loading) {
    ruleList = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: palette.brand }} />
      </Box>
    );
  } else if (rules.length === 0) {
    ruleList = (
      <Box sx={{ p: 2.5 }}>
        <EmptyState
          icon={<PercentOutlinedIcon sx={{ fontSize: 30 }} />}
          title="No commission rules"
          description="Without a rule, creators earn nothing on your products — so none of them will promote you."
          action={
            <PrimaryButton startIcon={<AddIcon />} onClick={openCreate}>
              Create your first rule
            </PrimaryButton>
          }
        />
      </Box>
    );
  } else {
    ruleList = (
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 820 }}>
          <TableHead>
            <TableRow sx={HEAD_ROW_SX}>
              <TableCell>Rule</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell align="right">Rate</TableCell>
              <TableCell align="right">Min order</TableCell>
              <TableCell align="right">Cap</TableCell>
              <TableCell align="right">Window</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.map((rule) => (
              <TableRow key={rule.public_id} hover sx={BODY_ROW_SX}>
                <TableCell sx={{ fontSize: 14, fontWeight: 700, color: palette.ink }}>
                  {rule.name}
                </TableCell>
                <TableCell>
                  <ScopePill scope={rule.scope} />
                  {/* Scope says WHAT a rule targets; it does not say WHO it
                      pays. A campaign rate is BRAND-scoped like any other, so
                      without this the list showed a 23% and a 10% rule side by
                      side, both BRAND, both active, with nothing to explain
                      why anyone would get one rather than the other. */}
                  {rule.campaign && (
                    <Tooltip
                      arrow
                      title={
                        `Only the ${rule.campaign.approved_creators} creator(s) `
                        + `approved for the campaign "${rule.campaign.title}" `
                        + 'earn this rate. Everyone else falls back to your '
                        + 'standing rule.'
                      }
                    >
                      <Chip
                        size="small"
                        label={`Campaign · ${rule.campaign.approved_creators}`}
                        sx={{
                          ml: 0.75, height: 20, fontSize: 11, fontWeight: 700,
                          color: palette.purple, bgcolor: palette.purpleSoft,
                        }}
                      />
                    </Tooltip>
                  )}
                </TableCell>
                {/* The rate is the number everyone came to read, so it carries
                    the most weight in the row. */}
                <TableCell
                  align="right"
                  sx={{ ...typography.money, fontWeight: 700, color: palette.ink }}
                >
                  {rule.rate_type === 'PERCENT'
                    ? `${rule.rate_value}%`
                    : `${rule.rate_value} flat/unit`}
                </TableCell>
                <TableCell align="right" sx={{ ...typography.money, color: palette.inkSoft }}>
                  {rule.min_order_value || '—'}
                </TableCell>
                <TableCell align="right" sx={{ ...typography.money, color: palette.inkSoft }}>
                  {rule.max_commission_amount || '—'}
                </TableCell>
                <TableCell align="right" sx={{ ...typography.money, color: palette.muted }}>
                  {rule.attribution_window_days}d
                </TableCell>
                <TableCell>
                  <StatusPill
                    status={rule.is_active ? 'ACTIVE' : 'DRAFT'}
                    label={rule.is_active ? 'Active' : 'Inactive'}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Box>
      <SectionCard
        title="Commission rules"
        subtitle="Most specific rule wins: product beats category beats brand."
        noPadBody
        actions={
          <PrimaryButton startIcon={<AddIcon />} onClick={openCreate}>
            New rule
          </PrimaryButton>
        }
      >
        {ruleList}
      </SectionCard>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: `${radius.lg}px`, boxShadow: shadow.modal, overflow: 'hidden' },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ px: 3, py: 2.25, borderBottom: `1px solid ${palette.line}`, bgcolor: palette.surfaceAlt }}
          >
            <Box
              sx={{
                width: 36, height: 36, flexShrink: 0,
                borderRadius: `${radius.sm}px`,
                bgcolor: palette.brandSoft, color: palette.brand,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <PercentOutlinedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography sx={typography.sectionTitle}>New commission rule</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3, bgcolor: palette.surface }}>
          <Grid container spacing={2.5} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Rule name"
                value={form.name}
                onChange={setField('name')}
                placeholder="e.g. Standard 10% on footwear"
                sx={FIELD_SX}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Scope"
                value={form.scope}
                onChange={setField('scope')}
                helperText={SCOPE_HELP[form.scope]}
                sx={FIELD_SX}
              >
                {['BRAND', 'CATEGORY', 'PRODUCT'].map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {form.scope === 'CATEGORY' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Category"
                  value={form.category}
                  onChange={setField('category')}
                  sx={FIELD_SX}
                >
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            {form.scope === 'PRODUCT' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Product"
                  value={form.product}
                  onChange={setField('product')}
                  sx={FIELD_SX}
                >
                  {products.map((p) => (
                    <MenuItem key={p.public_id} value={p.id || p.public_id}>
                      {p.title}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Rate type"
                value={form.rate_type}
                onChange={setField('rate_type')}
                sx={FIELD_SX}
              >
                <MenuItem value="PERCENT">Percentage of item total</MenuItem>
                <MenuItem value="FLAT">Flat amount per unit</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={form.rate_type === 'PERCENT' ? 'Rate (%)' : 'Amount per unit'}
                value={form.rate_value}
                onChange={setField('rate_value')}
                inputProps={{ inputMode: 'decimal' }}
                sx={FIELD_SX}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Minimum order value"
                value={form.min_order_value}
                onChange={setField('min_order_value')}
                helperText="Below this, nothing is payable"
                inputProps={{ inputMode: 'decimal' }}
                sx={FIELD_SX}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Maximum commission"
                value={form.max_commission_amount}
                onChange={setField('max_commission_amount')}
                helperText="Caps percentage rules on expensive items"
                inputProps={{ inputMode: 'decimal' }}
                sx={FIELD_SX}
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ borderRadius: `${radius.sm}px` }}>
                To change a rate later, end-date this rule and add a new one.
                Editing the rate does not alter commissions already earned —
                those snapshot their rule — but it silently changes every
                future one.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{ px: 3, py: 2, borderTop: `1px solid ${palette.line}`, bgcolor: palette.surfaceAlt }}
        >
          <SecondaryButton onClick={() => setDialogOpen(false)}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleSave} loader={saving}>
            Create rule
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CommissionRules;
